import { option } from 'fp-ts';
import { pipe } from 'fp-ts/function';

import { convertToMultiCurrencyAmount, createZeroAmount } from '@mews-distributor/common';

import { getCategoryServiceId } from '../../../selectors/views/categories';
import { ApplicationState } from '../../../types';
import { Category } from '../../../types/data';
import { WithCategoryId } from '../../../types/selectors/selectorPropTypes';
import { getCurrencyCode } from '../../app/selectors';

import { getCurrentDateServiceCategoryLowestPrice } from './servicePricing';

interface WithCategory {
    category: Category;
}

export const getSelectedEnterpriseCategoryLowestPrice = (
    state: ApplicationState,
    { categoryId, filterOnlyRefundable = false }: WithCategoryId & { filterOnlyRefundable?: boolean }
) => {
    const currencyCode = getCurrencyCode(state);
    if (currencyCode == null) {
        return null;
    }
    return pipe(
        option.fromNullable(getCategoryServiceId(state, { categoryId })),
        option.flatMap((serviceId) =>
            getCurrentDateServiceCategoryLowestPrice(state, {
                categoryId,
                serviceId,
                currencyCode,
                filterOnlyRefundable: Boolean(filterOnlyRefundable),
            })
        ),
        option.map((amount) => convertToMultiCurrencyAmount(amount)),
        option.getOrElse(() => convertToMultiCurrencyAmount(createZeroAmount(currencyCode)))
    );
};

export const getSelectedEnterpriseCategoryCityTaxIncludedInPriceOption = (
    state: ApplicationState,
    { category }: WithCategory
) => {
    const currencyCode = getCurrencyCode(state);
    if (currencyCode == null) {
        return option.none;
    }
    return pipe(
        option.fromNullable(getCategoryServiceId(state, { categoryId: category.id })),
        option.flatMap((serviceId) =>
            getCurrentDateServiceCategoryLowestPrice(state, {
                categoryId: category.id,
                serviceId,
                currencyCode,
            })
        ),
        option.map(({ cityTaxIncludedInPrice }) => cityTaxIncludedInPrice)
    );
};

export const getSelectedEnterpriseCategoryFeeProducts = (
    state: ApplicationState,
    { categoryId }: { categoryId: string }
) => {
    const currencyCode = getCurrencyCode(state);
    if (currencyCode == null) {
        return null;
    }
    return pipe(
        option.fromNullable(getCategoryServiceId(state, { categoryId })),
        option.flatMap((serviceId) =>
            getCurrentDateServiceCategoryLowestPrice(state, {
                categoryId,
                serviceId,
                currencyCode,
            })
        ),
        option.map((product) => product.feeProducts ?? []),
        option.getOrElseW(() => [])
    );
};
